'use client';

import { ReactNode, createContext, useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash-es/debounce';
import { Background } from '../types';
import { ALL_SECTION_INDEX } from './types';

const getNextBackground = (
  background: Background,
  isForYouSectionFeatureEnabled: boolean,
): Background => {
  if (isForYouSectionFeatureEnabled) {
    switch (background) {
      case 'light':
        return 'transparent';
      case 'transparent':
        return 'dark';
      case 'dark':
        return 'light';
    }
  } else {
    switch (background) {
      case 'transparent':
        return 'light';
      case 'light':
        return 'dark';
      case 'dark':
        return 'transparent';
    }
  }
};

type Visibilities = Map<number, Boolean>;
type Backgrounds = Map<number, Background | undefined>;

// loop through the visible backgrounds and assign the next background based on the previous one
const determineBackgrounds = (
  initialBackground: Background,
  visibilities: Visibilities,
  isForYouSectionFeatureEnabled: boolean,
): Backgrounds => {
  let lastAssignedBackground: Background = initialBackground;
  const backgrounds: Backgrounds = new Map<number, Background | undefined>();

  const keys = Array.from(visibilities.keys()).sort((a, b) => a - b);

  keys.forEach((sectionIndex) => {
    const visible = visibilities.get(sectionIndex);
    if (visible) {
      const backgroundToAssign = getNextBackground(
        lastAssignedBackground,
        isForYouSectionFeatureEnabled,
      );
      backgrounds.set(sectionIndex, backgroundToAssign);
      lastAssignedBackground = backgroundToAssign;
    } else {
      backgrounds.set(sectionIndex, undefined);
    }
  });

  // set the All Inventory background based on the last assigned background, but never to dark
  backgrounds.set(
    ALL_SECTION_INDEX,
    lastAssignedBackground === 'transparent' ? 'light' : 'transparent',
  );
  return backgrounds;
};

export const SectionColorContext = createContext<{
  setVisiblityAtIndex: (index: number, visible: boolean) => void;
  backgrounds?: Backgrounds;
}>({
  setVisiblityAtIndex: () => {},
});

export const SectionColorProvider = ({
  children,
  initialBackground,
  isForYouSectionFeatureEnabled,
}: {
  children: ReactNode;
  initialBackground: Background;
  isForYouSectionFeatureEnabled: boolean;
}): JSX.Element => {
  const [backgrounds, setBackgrounds] = useState<Backgrounds>();
  const [visibilities, setVisibilities] = useState<Visibilities>();

  const debouncedSetBackgrounds = useRef(
    debounce((visibilities: Visibilities) => {
      setBackgrounds(
        determineBackgrounds(initialBackground, visibilities, isForYouSectionFeatureEnabled),
      );
    }, 100),
  );

  const setVisiblityAtIndex = useCallback((index: number, visible: boolean) => {
    // let each CollectionVisibilityProvider report its own visiblity
    setVisibilities((prev) => {
      const newVisibilities = new Map(prev);
      newVisibilities.set(index, visible);
      return newVisibilities;
    });
  }, []);

  useEffect(() => {
    if (!visibilities) return;
    // then determine the backgrounds based on the visibilities
    debouncedSetBackgrounds.current(visibilities);
  }, [visibilities]);

  return (
    <SectionColorContext.Provider
      value={{
        setVisiblityAtIndex,
        backgrounds,
      }}
    >
      {children}
    </SectionColorContext.Provider>
  );
};
